import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { useState } from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { LocalStorageKeys } from 'utils/constants';

const endpoint = 'pos/users/me';
export const getMeEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [typeof endpoint];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useMeApi(options?: Options): ReturnType {
  const [isLoading, setIsLoading] = useState(false);
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();

  const queryFn = async () => {
    try {
      setIsLoading(true);

      const { data } = await http.get(endpoint);

      // Store the linked POS in localStorage in case of having one linked POS
      const hasSinglePOSIntegrated = data?.vendors?.length === 1;

      if (hasSinglePOSIntegrated) {
        localStorage.setItem(LocalStorageKeys.vendor, data?.vendors[0]?.name.toString().toLowerCase());
      }

      setIsLoading(false);

      return { isLoading, ...data };
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: 'Something went wrong! Try again later',
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
